import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plane, ShieldCheck, FileText } from "lucide-react";
import api from "@/lib/api";
import { formatMoney } from "@/lib/format";

const FALLBACK = [
  { id: "f1", name: "International Flight Booking", description: "Discounted fares to Dhaka, Sylhet, Chittagong and worldwide. Economy, premium, business.", price: 0, currency: "SEK", category: "flight" },
  { id: "f2", name: "Schengen Visa Assistance", description: "Document preparation, appointment booking, interview coaching for Schengen visas.", price: 1200, currency: "SEK", category: "visa" },
  { id: "f3", name: "Bangladesh e-Visa Support", description: "Help travellers from Sweden get their Bangladesh visa hassle-free.", price: 950, currency: "SEK", category: "visa" },
  { id: "f4", name: "Group & Family Bookings", description: "Family, religious or corporate group bookings — fully managed from quote to return.", price: 0, currency: "SEK", category: "flight" },
  { id: "f5", name: "Travel Insurance", description: "Comprehensive travel insurance policies through our trusted partners.", price: 220, currency: "SEK", category: "other" },
  { id: "f6", name: "Hotel & Airport Transfers", description: "Hotel bookings and airport transfers in Dhaka, Stockholm and beyond.", price: 0, currency: "SEK", category: "other" },
];

const CAT_ICON = {
  flight: Plane,
  visa: ShieldCheck,
  other: FileText,
};

export default function Services() {
  const [services, setServices] = useState(null);

  useEffect(() => {
    api.get("/public/services").then((r) => setServices(r.data)).catch(() => setServices([]));
  }, []);

  const items = services && services.length > 0 ? services : FALLBACK;

  return (
    <div data-testid="services-page">
      <section className="relative">
        <div className="bg-stage" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="bg-grid" />
          <div className="orb orb-green" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 pt-20 pb-12 sm:pt-28 sm:pb-16">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[color:var(--ivory-muted)] mb-3">Services</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            Every kind of journey,<br />in trustworthy hands.
          </h1>
          <p className="mt-5 max-w-2xl text-[color:var(--ivory-muted)] text-base sm:text-lg">
            From a quick flight to a once-in-a-lifetime pilgrimage — we handle the moving parts so you can enjoy the trip.
          </p>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((s) => {
            const Icon = CAT_ICON[s.category] || Plane;
            return (
              <article
                key={s.id}
                className="rounded-2xl border border-white/10 bg-white/[0.02] hover:border-[color:var(--bd-green)]/30 transition p-6"
                data-testid={`service-${s.id}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[color:var(--bd-green)]/15 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[color:var(--bd-green-bright)]" />
                  </div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)]">{s.category}</div>
                </div>
                <h3 className="font-display text-xl mb-2">{s.name}</h3>
                <p className="text-sm text-[color:var(--ivory-muted)] mb-5 leading-relaxed">{s.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[color:var(--bd-green-bright)]">
                    {Number(s.price) > 0 ? `From ${formatMoney(s.price, s.currency || "SEK")}` : "Custom quote"}
                  </span>
                  <Link to="/contact" className="text-xs uppercase tracking-[0.2em] text-white hover:text-[color:var(--bd-green-bright)]">
                    Enquire →
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
